/**
 * Gegenstück zum Service Worker: holt das beim Teilen zwischengelagerte
 * Material wieder heraus.
 *
 * Der Ablauf beim Teilen aus einer anderen App:
 *   Android → POST /share-target → Service Worker legt ab → Weiterleitung
 *   → /teilen lädt → liest hier mit → Ziel wählen → ablegen und aufräumen
 *
 * Gelesen und geräumt wird getrennt: Die Auswahlseite muss zeigen können, was
 * angekommen ist, bevor entschieden ist, wohin es geht. Weggeräumt wird erst,
 * wenn es angekommen ist – sonst wäre ein Verweis nach einem Fehlversuch weg.
 *
 * Jede App hat ihren eigenen Zwischenspeicher (`SHARE_CACHE` im Manager,
 * `DOCBASE_SHARE_CACHE` in der DocBase); welcher gemeint ist, sagt der
 * Aufruf.
 */

import { uploadMimeType, type Bereich } from '@manager/shared'

import {
  DOCBASE_SHARE_CACHE,
  SHARE_CACHE,
  SHARE_FILENAME_HEADER,
  SHARE_FILE_PREFIX,
  SHARE_LOG_KEY,
  SHARE_TEXT_KEY,
  type ShareProtokoll,
} from './shareConstants'
import type { SharedText } from './shareNote'

/** Der Zwischenspeicher der App, zu der ein Bereich gehört. */
export function shareCacheFor(bereich: Bereich): string {
  return bereich === 'docbase' ? DOCBASE_SHARE_CACHE : SHARE_CACHE
}

export interface SharedContent extends SharedText {
  files: File[]
  /** Was der Worker beim Teilen festgehalten hat – `null`, wenn es keins gibt. */
  protokoll?: ShareProtokoll | null
}

export const LEER: SharedContent = { files: [], title: '', text: '', url: '' }

/**
 * Selbst gewählte Dateien zum Geteilten dazu – wenn die andere App beim
 * Teilen keine (oder eine leere) mitgab und die Datei deshalb auf der
 * Auswahlseite gewählt wird.
 *
 * Dazu, nicht anstelle: Was an Titel, Text und Verweis ankam, bleibt – sonst
 * stünde in der Notiz danach nur die Datei. Das Protokoll fällt weg; die
 * Meldung „kam leer an" ist mit der gewählten Datei erledigt. Der Typ wird
 * geprüft wie beim Abholen aus dem Zwischenspeicher.
 */
export function withFiles(content: SharedContent, files: readonly File[]): SharedContent {
  const normalisiert = files.map((file) => {
    const typ = uploadMimeType(file.type, file.name)
    return typ === file.type ? file : new File([file], file.name, { type: typ })
  })
  return { ...content, files: [...content.files, ...normalisiert], protokoll: null }
}

/** Ist überhaupt etwas angekommen? */
export function istLeer(content: SharedContent): boolean {
  return (
    content.files.length === 0 && content.title === '' && content.text === '' && content.url === ''
  )
}

/**
 * Liest das Geteilte, ohne es zu verbrauchen.
 *
 * Bleibt etwas liegen, weil jemand die Auswahl abbricht, ist das kein Schaden:
 * Der Worker leert den Zwischenspeicher zu Beginn jedes neuen Teilens.
 */
export async function readSharedContent(cacheName: string): Promise<SharedContent> {
  const cache = await oeffnen(cacheName)
  if (!cache) return LEER

  const files: File[] = []
  let text: SharedText = { title: '', text: '', url: '' }
  let protokoll: ShareProtokoll | null = null

  for (const key of await cache.keys()) {
    const response = await cache.match(key)
    if (!response) continue

    const { pathname } = new URL(key.url)

    if (pathname === SHARE_TEXT_KEY) {
      text = await lesenAlsText(response)
      continue
    }

    if (pathname === SHARE_LOG_KEY) {
      protokoll = await lesenAlsProtokoll(response)
      continue
    }

    if (!pathname.startsWith(SHARE_FILE_PREFIX)) continue
    files.push(await lesenAlsDatei(response))
  }

  return { files, ...text, protokoll }
}

/**
 * Leert das Zwischenlager, ohne das Geteilte zu verwenden – nach dem Ablegen,
 * oder wenn jemand die Auswahl verwirft.
 */
export async function discardSharedContent(cacheName: string): Promise<void> {
  if (!('caches' in globalThis)) return
  try {
    await caches.delete(cacheName)
  } catch {
    // Kein Cache vorhanden – nichts zu tun.
  }
}

async function oeffnen(cacheName: string): Promise<Cache | null> {
  if (!('caches' in globalThis)) return null
  try {
    return await caches.open(cacheName)
  } catch {
    return null
  }
}

/**
 * Eine geteilte Datei – mit einem Typ, den der Server annimmt.
 *
 * Was Android als Typ mitgibt, hängt an der App, aus der geteilt wird: Eine
 * Galerie meldet ein Foto als „image/jpg", ein Dateimanager ein PDF als
 * „application/octet-stream", manche gar nichts. Genau daran scheiterte das
 * Teilen oft mit „Dateityp wird nicht unterstützt", obwohl die Datei passte.
 * Der Name verrät meist, was es ist; `uploadMimeType` nimmt, was stimmt.
 */
async function lesenAlsDatei(response: Response): Promise<File> {
  const blob = await response.blob()
  const encodedName = response.headers.get(SHARE_FILENAME_HEADER)
  const name = encodedName ? decodeURIComponent(encodedName) : 'Geteiltes Dokument'
  return new File([blob], name, { type: uploadMimeType(blob.type, name) })
}

async function lesenAlsProtokoll(response: Response): Promise<ShareProtokoll | null> {
  try {
    const roh = (await response.json()) as Partial<ShareProtokoll>
    return Array.isArray(roh.felder) ? { felder: roh.felder } : null
  } catch {
    return null
  }
}

async function lesenAlsText(response: Response): Promise<SharedText> {
  try {
    const roh = (await response.json()) as Partial<SharedText>
    return {
      title: typeof roh.title === 'string' ? roh.title : '',
      text: typeof roh.text === 'string' ? roh.text : '',
      url: typeof roh.url === 'string' ? roh.url : '',
    }
  } catch {
    // Unlesbar abgelegt: lieber ohne Text weitermachen, als das ganze Teilen
    // an einer kaputten Zeile scheitern zu lassen.
    return { title: '', text: '', url: '' }
  }
}
